// What the edge's 429 on POST /v1/friends/requests means to the person who sent it: a decline's
// 30 day cooldown, the 50 a day, or a burst. The wait comes from `retryAfterSeconds`, never from
// the device clock, and a refusal is final for now, so it is never queued for a later retry.
import { FriendRequestRefusal } from "@proef/contract";
import { EdgeApiError } from "../edgeApi";
import type { Locale, MessageKey } from "../i18n";

/** The refusal an error carries, or undefined for any other failure (offline, 5xx, other 429s). */
export function friendRequestRefusal(error: unknown): FriendRequestRefusal | undefined {
  if (!(error instanceof EdgeApiError) || error.status !== 429) return undefined;
  const parsed = FriendRequestRefusal.safeParse(error.body);
  return parsed.success ? parsed.data : undefined;
}

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86_400;

/** "in 28 days", "over 4 uur": the wait rounded up to the unit that reads naturally. */
export function retryIn(seconds: number, locale: Locale): string {
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
  if (seconds >= DAY) return format.format(Math.ceil(seconds / DAY), "day");
  if (seconds >= HOUR) return format.format(Math.ceil(seconds / HOUR), "hour");
  return format.format(Math.max(1, Math.ceil(seconds / MINUTE)), "minute");
}

const KEYS: Readonly<Record<FriendRequestRefusal["code"], MessageKey>> = {
  decline_cooldown: "social.friends.request.declined",
  daily_limit: "social.friends.request.dailyLimit",
  rate_limit: "social.friends.request.burst",
};

/** The sentence for a refusal, in the reader's language, with the wait filled in. */
export function friendRequestMessage(
  refusal: FriendRequestRefusal,
  t: (key: MessageKey, vars?: Record<string, string | number>) => string,
  locale: Locale,
): string {
  return t(KEYS[refusal.code], { when: retryIn(refusal.retryAfterSeconds, locale) });
}
